import { PanoramicViewer, ThumbnailQuality, VideoUtil } from '@ibe/components';
import { ApiExternalContent, ApiImage, ApiImageContainer, ApiImageType, ApiMedia } from '@ibe/api';
import { ReactImageGalleryItem } from 'react-image-gallery';
import React, { JSX } from 'react';
import YouTube, { YouTubePlayer } from 'react-youtube';
import { TikTokEmbed } from 'react-social-media-embed';

abstract class GalleryUtils {
  private static readonly ALLOWED_DOMAINS = {
    youtube: ['youtube.com', 'youtu.be'],
    tiktok: ['tiktok.com', 'tiktokv.com'],
    matterport: ['matterport.com'],
    threshold360: ['threshold360.com'],
    visim: ['visim.eu'],
    merlinTour: ['tour.legoland.de', 'tour.heide-park.de']
  };

  private static readonly TIKTOK_EMBED_WIDTH = 325;

  private static readonly TIKTOK_EMBED_HEIGHT = 757;

  public static readonly getImagesForCarousel = (media: ApiMedia): ApiImageContainer[] => {
    const images: ApiImageContainer[] = [];
    if (!media) {
      return images;
    }

    images.push(...this.getImages(media, false));

    if (media.videos) {
      for (const video of media.videos) {
        const thumbnail: ApiImage = {
          url: VideoUtil.getVideoThumbnailUrl(video.url, ThumbnailQuality.LOW),
          mimeType: 'image/jpeg'
        };
        images.push({
          large: thumbnail,
          extraSmall: thumbnail,
          small: thumbnail,
          offer: thumbnail,
          index: video.index,
          imageType: ApiImageType.VIDEOTHUMBNAIL
        } as ApiImageContainer);
      }
    }
    if (media.externalContents) {
      for (const ec of media.externalContents) {
        const thumbnail = ec.thumbnailImage;
        if (thumbnail) {
          images.push({ ...thumbnail, index: ec.index });
        }
      }
    }
    return images.sort(
      (a, b) => (a.index ?? Number.MAX_SAFE_INTEGER) - (b.index ?? Number.MAX_SAFE_INTEGER)
    );
  };

  public static readonly getImageGalleryItems = (
    media: ApiMedia,
    onYoutubeReady?: (videoUrl: string, player: YouTubePlayer) => void
  ) => {
    const items = [
      ...this.getImages(media, true).map(image => ({
        original: image?.large?.url,
        thumbnail: image?.large?.url,
        index: image.index
      })),
      ...this.getExternalContents(media).map(externalContent => {
        return {
          original: externalContent?.url,
          thumbnail: this.getThumbnail(externalContent),
          renderItem: (item: ReactImageGalleryItem) =>
            this.buildExternalComponent(item, onYoutubeReady),
          index: externalContent.index
        };
      }),
      ...this.getPanoramicImages(media).map(image => ({
        original: image?.large?.url || '',
        thumbnail: image?.large?.url || '',
        renderItem: this.buildPanoramicImageComponentFromReactImageGalleryItem.bind(this),
        index: image.index
      }))
    ];

    return items.sort(
      (a, b) => (a.index ?? Number.MAX_SAFE_INTEGER) - (b.index ?? Number.MAX_SAFE_INTEGER)
    );
  };

  private static readonly getThumbnail = (externalContent: ApiExternalContent): string => {
    const thumbnail =
      externalContent?.thumbnailImage?.small?.url || externalContent?.thumbnailImage?.large?.url;
    if (
      !thumbnail &&
      externalContent?.url &&
      this.isAllowedUrl(externalContent.url, this.ALLOWED_DOMAINS.youtube)
    ) {
      return VideoUtil.getVideoThumbnailUrl(externalContent.url, ThumbnailQuality.LOW);
    }
    return thumbnail || './Images/default-image.webp';
  };

  private static isAllowedUrl(urlString: string, allowedHosts: string[]): boolean {
    try {
      const url = new URL(urlString);
      return (
        ['https:', 'http:'].includes(url.protocol) &&
        allowedHosts.some(host => url.hostname === host || url.hostname.endsWith(`.${host}`))
      );
    } catch {
      return false;
    }
  }

  private static readonly getImages = (media: ApiMedia, filtered: boolean): ApiImageContainer[] => {
    const images = [];
    if (media.mainImage?.large?.url || media.mainImage?.small?.url) {
      images.push({ ...media.mainImage, index: 0 });
    }
    if (media.images) {
      const filteredImages = filtered
        ? media.images.filter(
            image =>
              image.imageType === null ||
              image.imageType === undefined ||
              image.imageType === ApiImageType.DEFAULT
          )
        : media.images;
      images.push(...filteredImages);
    }
    return images;
  };

  private static getExternalContents(media: ApiMedia): ApiExternalContent[] {
    return media.externalContents || [];
  }

  private static readonly getPanoramicImages = (media: ApiMedia): ApiImageContainer[] => {
    return media.images.filter(image => image.imageType === ApiImageType.VIEW360) || [];
  };

  private static readonly buildPanoramicImageComponentFromReactImageGalleryItem = (
    item: ReactImageGalleryItem
  ): JSX.Element => {
    return item?.original ? <PanoramicViewer image={item.original} /> : <div />;
  };

  private static readonly buildExternalComponent = (
    item: ReactImageGalleryItem,
    onYoutubeReady?: (videoUrl: string, player: YouTubePlayer) => void
  ): JSX.Element => {
    const scale = Math.min(
      window.innerWidth / this.TIKTOK_EMBED_WIDTH,
      window.innerHeight / this.TIKTOK_EMBED_HEIGHT
    );

    if (item.original.endsWith('.mp4') && this.isAllowedUrl(item.original, [])) {
      return (
        <div key={item.original} className="slider__screen">
          <video width="100%" height="100%" controls>
            <source
              src={item.original}
              type="video/mp4"
              style={{ width: '100%', height: '100%' }}
            />
          </video>
        </div>
      );
    }

    if (this.isAllowedUrl(item.original, this.ALLOWED_DOMAINS.youtube)) {
      return (
        <div key={item.original} className="slider__screen">
          <YouTube
            videoId={VideoUtil.getYoutubeId(item.original)}
            opts={{ width: '100%', height: '100%' }}
            onReady={event => {
              if (onYoutubeReady) {
                onYoutubeReady(item.original, event.target);
              }
            }}
          />
        </div>
      );
    }

    if (this.isAllowedUrl(item.original, this.ALLOWED_DOMAINS.tiktok)) {
      return (
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            margin: '0 auto',
            overflow: 'hidden'
          }}
        >
          <TikTokEmbed
            url={item.original}
            width={this.TIKTOK_EMBED_WIDTH}
            height={this.TIKTOK_EMBED_HEIGHT}
          />
        </div>
      );
    }

    if (this.isAllowedUrl(item.original, this.ALLOWED_DOMAINS.matterport)) {
      return (
        <iframe
          src={item.original}
          width="100%"
          height="100%"
          allowFullScreen
          allow="xr-spatial-tracking"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Matterport 3D Tour"
          style={{ border: 0 }}
        />
      );
    }

    if (this.isAllowedUrl(item.original, this.ALLOWED_DOMAINS.threshold360)) {
      return (
        <iframe
          src={item.original}
          width="100%"
          height="100%"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
          title="ViewerThreshold360 Video"
          style={{ border: 0 }}
        />
      );
    }

    if (this.isAllowedUrl(item.original, this.ALLOWED_DOMAINS.visim)) {
      return (
        <iframe
          src={item.original}
          width="100%"
          height="100%"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Visim 3D Tour"
          style={{ border: 0, display: 'none' }}
          onLoad={el => {
            el.currentTarget.style.display = 'block';
          }}
        />
      );
    }

    if (this.isAllowedUrl(item.original, this.ALLOWED_DOMAINS.merlinTour)) {
      return (
        <iframe
          src={item.original}
          width="100%"
          height="100%"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin allow-presentation"
          referrerPolicy="strict-origin-when-cross-origin"
          title="Merlin 3D Tour"
          style={{ border: 0 }}
        />
      );
    }

    return <div />;
  };
}

export default GalleryUtils;
